// /admin/ads → "איך זה עובד": the three ways an ad turns into a customer, explained step by
// step, compared side by side on real numbers (from the same per-ad data as the ads table),
// plus the two test flows (sign-up page / WhatsApp) with a one-click reset.
import {FC, useEffect, useState} from 'react'
import {getFlow, Launch, resetTestNumber} from './_requests'

const ils = (n: number) => `₪${Math.round(n || 0).toLocaleString('he-IL')}`
const errMsg = (e: any) => e?.response?.data?.error || e?.message || 'שגיאה'

type PathKey = 'website' | 'lead_form' | 'whatsapp'

const PATHS: {
  key: PathKey
  title: string
  icon: string
  color: string
  customer: string[]
  system: string[]
}[] = [
  {
    key: 'website',
    title: 'דף הרשמה',
    icon: '🌐',
    color: '#2b70fa',
    customer: [
      'רואה רילס בפייסבוק / אינסטגרם ולוחץ "הרשמה"',
      'נפתח טופס הרשמה: גוגל / פייסבוק בלחיצה, או שם + טלפון',
      'נכנס למערכת. בגוגל/פייסבוק — מזין טלפון לפני שממשיך',
      'מקבל וואטסאפ ברוכים הבאים עם סיסמה וקישור כניסה',
      'מחבר פרופיל פייסבוק ומתחיל לפרסם',
    ],
    system: [
      'המודעה שומרת מאיזו מודעה הגיע (utm + fbclid)',
      'החשבון נפתח עם ימי ניסיון ומספר וירטואלי',
      'ההרשמה מדווחת לפייסבוק (CAPI) — כדי שימצא עוד נרשמים כאלה',
      'לא חיבר פרופיל? מיילי תזכורת לפי התזמון בלשונית "תהליך הרשמה"',
    ],
  },
  {
    key: 'lead_form',
    title: 'טופס פייסבוק',
    icon: '📝',
    color: '#7c3aed',
    customer: [
      'רואה רילס ולוחץ "הרשמה"',
      'נפתח טופס בתוך פייסבוק — שם, טלפון ואימייל כבר ממולאים',
      'לוחץ "שליחה" ונשאר בפייסבוק',
      'תוך כ-3 דקות מקבל וואטסאפ עם סיסמה וקישור כניסה',
      'נכנס בקישור, מחבר פרופיל ומתחיל לפרסם',
    ],
    system: [
      'השרת מושך לידים חדשים כל 3 דקות',
      'כל ליד → חשבון ניסיון אוטומטי (אם המספר כבר לקוח — מסומן "כבר לקוח")',
      'הכי מעט מאמץ ללקוח → בדרך כלל הכי זול לליד, אבל פחות מחויבות',
      'מיילי תזכורת כמו בדף ההרשמה (אם השאיר אימייל)',
    ],
  },
  {
    key: 'whatsapp',
    title: 'וואטסאפ',
    icon: '💬',
    color: '#16a34a',
    customer: [
      'רואה רילס ולוחץ "שליחת הודעה"',
      'נפתח צ׳אט עם 054-615-5952 והבוט עונה מיד',
      'עונה מה שמו ומה הוא מפרסם',
      'מקבל באותו צ׳אט חשבון, סיסמה וקישור כניסה',
      'אפשר לשאול שאלות — נציג עונה מלשונית "וואטסאפ"',
    ],
    system: [
      'כל שיחה נשמרת עם המודעה שהביאה אותה',
      'הבוט פותח חשבון ומדווח לפייסבוק (CAPI להודעות)',
      'נציג שעונה מהתיבה עוצר את הבוט באותה שיחה',
      '72 שעות של הודעות חינם משיחה שהתחילה ממודעה',
    ],
  },
]

type Row = {
  spend: number
  leads: number
  signups: number
  connected: number
  paid: number
  revenue: number
  ads: number
}

const HowItWorks: FC<{launches: Launch[]}> = ({launches}) => {
  const [testNumbers, setTestNumbers] = useState<string[]>([])
  const [resetPhone, setResetPhone] = useState('')
  const [resetMsg, setResetMsg] = useState<{ok: boolean; text: string} | null>(null)
  const [busy, setBusy] = useState(false)
  const [link, setLink] = useState('https://console.fbzipper.com/auth/register')

  useEffect(() => {
    getFlow()
      .then(({data}) => {
        const nums = (data.flow.whatsapp_bot?.test_numbers || []).map((n) =>
          n.startsWith('972') ? '0' + n.slice(3) : n
        )
        setTestNumbers(nums)
        setResetPhone(nums[0] || '')
        setLink(data.flow.default_link)
      })
      .catch(() => {})
  }, [])

  const rows: Record<PathKey, Row> = {
    website: {spend: 0, leads: 0, signups: 0, connected: 0, paid: 0, revenue: 0, ads: 0},
    lead_form: {spend: 0, leads: 0, signups: 0, connected: 0, paid: 0, revenue: 0, ads: 0},
    whatsapp: {spend: 0, leads: 0, signups: 0, connected: 0, paid: 0, revenue: 0, ads: 0},
  }
  launches.forEach((l) => {
    const k = (l.destination || 'website') as PathKey
    if (!rows[k]) return
    const r = rows[k]
    r.ads++
    r.spend += l.meta?.spend || 0
    r.leads +=
      (l.funnel.form_leads || 0) +
      (l.funnel.wa_conversations || 0) +
      (k === 'website' ? l.meta?.clicks || 0 : 0)
    r.signups += l.funnel.signups
    r.connected += l.funnel.connected
    r.paid += l.funnel.paid
    r.revenue += l.funnel.revenue
  })
  const cpp = (k: PathKey) => (rows[k].paid ? rows[k].spend / rows[k].paid : Infinity)
  const best = (['website', 'lead_form', 'whatsapp'] as PathKey[])
    .filter((k) => rows[k].paid > 0)
    .sort((a, b) => cpp(a) - cpp(b))[0]

  const doReset = async () => {
    setBusy(true)
    setResetMsg(null)
    try {
      const {data} = await resetTestNumber(resetPhone)
      setResetMsg({
        ok: true,
        text: `אופס: ${data.messages_deleted} הודעות נמחקו, ${data.accounts_retired} חשבונות בדיקה הושבתו. אפשר להתחיל שוב את שתי הבדיקות.`,
      })
    } catch (e) {
      setResetMsg({ok: false, text: errMsg(e)})
    } finally {
      setBusy(false)
    }
  }

  const testLink = `${link}${
    link.includes('?') ? '&' : '?'
  }utm_source=test&utm_medium=flow_test&utm_campaign=landing_test`
  const waLink = `https://wa.me/972546155952?text=${encodeURIComponent(
    'היי, אשמח לשמוע על FBZipper'
  )}`

  return (
    <div>
      <h4 className='fw-bold mb-1'>שלוש הדרכים ממודעה ללקוח</h4>
      <div className='text-muted fs-7 mb-4'>
        כל מודעה שולחת לאחת מהדרכים (בוחרים ביצירת המודעה). כולן מגיעות לאותו מקום: חשבון ניסיון →
        חיבור פרופיל → תשלום.
      </div>
      <div
        className='d-grid gap-4 mb-6'
        style={{gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))'}}
      >
        {PATHS.map((p) => (
          <div
            key={p.key}
            className='card card-bordered'
            style={{borderTop: `4px solid ${p.color}`}}
          >
            <div className='card-body p-5'>
              <div className='fs-3 fw-bold mb-3'>
                {p.icon} {p.title}
              </div>
              <div className='fw-bold fs-7 mb-2' style={{color: p.color}}>
                מה הלקוח עובר
              </div>
              <ol className='fs-7 ps-4 mb-4'>
                {p.customer.map((s) => (
                  <li key={s} className='mb-1'>
                    {s}
                  </li>
                ))}
              </ol>
              <div className='fw-bold fs-7 mb-2 text-muted'>מה המערכת עושה ברקע</div>
              <ul className='fs-8 text-muted ps-4 mb-0'>
                {p.system.map((s) => (
                  <li key={s} className='mb-1'>
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      <h4 className='fw-bold mb-1'>מה עובד יותר טוב</h4>
      <div className='text-muted fs-7 mb-3'>
        מחושב מכל המודעות בלשונית "מודעות" (כולל מודעות קיימות שיובאו). ההחלטה לפי{' '}
        <b>עלות ללקוח משלם</b> — לא לפי עלות לליד: ליד זול שלא משלם הוא יקר.
      </div>
      <div className='table-responsive mb-6'>
        <table className='table table-row-bordered align-middle fs-7'>
          <thead>
            <tr className='fw-bold text-muted'>
              <th>דרך</th>
              <th>מודעות</th>
              <th>הוצאה</th>
              <th>קליקים / לידים / שיחות</th>
              <th>הרשמות</th>
              <th>חיברו פרופיל</th>
              <th>שילמו</th>
              <th>הכנסה</th>
              <th>עלות להרשמה</th>
              <th>עלות ללקוח משלם</th>
              <th>החזר (הכנסה ÷ הוצאה)</th>
            </tr>
          </thead>
          <tbody>
            {PATHS.map((p) => {
              const r = rows[p.key]
              return (
                <tr key={p.key} style={best === p.key ? {background: '#ecfdf3'} : undefined}>
                  <td className='fw-bold'>
                    {p.icon} {p.title}{' '}
                    {best === p.key && <span className='badge badge-success ms-1'>הכי משתלם</span>}
                  </td>
                  <td>{r.ads}</td>
                  <td>{ils(r.spend)}</td>
                  <td>{r.leads.toLocaleString('he-IL')}</td>
                  <td>{r.signups}</td>
                  <td>
                    {r.connected}
                    {r.signups ? (
                      <span className='text-muted fs-8'>
                        {' '}
                        ({Math.round((r.connected / r.signups) * 100)}%)
                      </span>
                    ) : null}
                  </td>
                  <td>
                    {r.paid}
                    {r.signups ? (
                      <span className='text-muted fs-8'>
                        {' '}
                        ({Math.round((r.paid / r.signups) * 100)}%)
                      </span>
                    ) : null}
                  </td>
                  <td className='fw-bold'>{ils(r.revenue)}</td>
                  <td>{r.signups ? ils(r.spend / r.signups) : '—'}</td>
                  <td className='fw-bold'>{r.paid ? ils(r.spend / r.paid) : '—'}</td>
                  <td>{r.spend ? `${(r.revenue / r.spend).toFixed(1)}×` : '—'}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className='text-muted fs-8 mb-6'>
        איך לבדוק: מריצים את אותו רילס בשתי הדרכים או שלוש, באותו תקציב ובאותו קהל, 5–7 ימים,
        ומשווים את "עלות ללקוח משלם". הרשמות נספרות רק מרגע שהמערכת החדשה עלתה, ומודעות ישנות שיובאו
        לא נושאות קוד מעקב — לכן ההרשמות שלהן לא ייספרו.
      </div>

      <h4 className='fw-bold mb-1'>🧪 בדיקה של שתי הדרכים</h4>
      <div className='text-muted fs-7 mb-3'>
        עוברים בעצמכם את מה שהלקוח עובר, ממספר בדיקה (לא המספר הרגיל שלכם). מספרי בדיקה מגדירים
        בלשונית "תהליך הרשמה" → בוט וואטסאפ.
        {testNumbers.length === 0 && <span className='text-danger'> עוד לא הוגדר מספר בדיקה.</span>}
      </div>
      <div
        className='d-grid gap-4 mb-4'
        style={{gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))'}}
      >
        <div className='card card-bordered p-5'>
          <div className='fw-bold fs-5 mb-2'>בדיקה 1 — דף הרשמה</div>
          <ol className='fs-7 ps-4'>
            <li>פותחים את הקישור בחלון גלישה בסתר (כדי לא להיות מחוברים)</li>
            <li>נרשמים עם מספר הבדיקה (או גוגל/פייסבוק ואז מספר הבדיקה)</li>
            <li>מוודאים: נכנסתם למערכת, הגיע וואטסאפ ברוכים הבאים</li>
            <li>אחרי 3 שעות בלי חיבור פרופיל — מגיע מייל התזכורת הראשון</li>
          </ol>
          <div className='d-flex gap-2'>
            <a className='btn btn-sm btn-primary' href={testLink} target='_blank' rel='noreferrer'>
              פתיחת דף ההרשמה (מצב בדיקה)
            </a>
            <button
              className='btn btn-sm btn-light'
              onClick={() => navigator.clipboard?.writeText(testLink)}
            >
              העתקת קישור
            </button>
          </div>
          <div className='text-muted fs-8 mt-2'>
            החשבון יסומן כבדיקה (utm_source=test) ולא ייספר במודעות.
          </div>
        </div>
        <div className='card card-bordered p-5'>
          <div className='fw-bold fs-5 mb-2'>בדיקה 2 — הרשמה בוואטסאפ</div>
          <ol className='fs-7 ps-4'>
            <li>מטלפון הבדיקה שולחים הודעה ל-054-615-5952 (או לוחצים על הקישור)</li>
            <li>עונים לבוט: שם, ואז מה מפרסמים</li>
            <li>מוודאים: הגיעו סיסמה וקישור כניסה, והקישור מכניס למערכת</li>
            <li>בלשונית "וואטסאפ" רואים את כל השיחה ואת החשבון שנפתח</li>
          </ol>
          <div className='d-flex gap-2'>
            <a className='btn btn-sm btn-success' href={waLink} target='_blank' rel='noreferrer'>
              פתיחת צ׳אט וואטסאפ
            </a>
            <button
              className='btn btn-sm btn-light'
              onClick={() => navigator.clipboard?.writeText(waLink)}
            >
              העתקת קישור
            </button>
          </div>
          <div className='text-muted fs-8 mt-2'>
            אפשר גם לשלוח <b dir='ltr'>#reset</b> מהטלפון כדי להתחיל מההתחלה.
          </div>
        </div>
      </div>
      <div className='card card-bordered p-4' style={{background: '#fff8eb'}}>
        <div className='d-flex align-items-center gap-2 flex-wrap'>
          <b className='fs-7'>איפוס מספר בדיקה (לשתי הבדיקות):</b>
          {testNumbers.length > 1 ? (
            <select
              className='form-select form-select-sm'
              style={{width: 180}}
              dir='ltr'
              value={resetPhone}
              onChange={(e) => setResetPhone(e.target.value)}
            >
              {testNumbers.map((n) => (
                <option key={n}>{n}</option>
              ))}
            </select>
          ) : (
            <input
              className='form-control form-control-sm'
              style={{width: 180}}
              dir='ltr'
              value={resetPhone}
              onChange={(e) => setResetPhone(e.target.value)}
            />
          )}
          <button
            className='btn btn-sm btn-warning'
            disabled={busy || !resetPhone}
            onClick={doReset}
          >
            {busy ? 'מאפס…' : 'איפוס'}
          </button>
          <span className='text-muted fs-8'>
            מוחק את שיחת הוואטסאפ ומשבית חשבונות בדיקה שנפתחו במספר הזה. חשבונות אמיתיים לא נגעים.
          </span>
        </div>
        {resetMsg && (
          <div className={`mt-2 fs-7 ${resetMsg.ok ? 'text-success' : 'text-danger'}`}>
            {resetMsg.text}
          </div>
        )}
      </div>
    </div>
  )
}

export default HowItWorks
